import React from 'react'
import {
  AdderTypes,
  dollars,
  LoanCalculations,
  dollarsToNum,
  Incentive,
  Adders,
  AmortizationSchedule,
  EquipmentType,
  IEquipmentOption,
  LOAN_FEE_DISCLOSURE,
  APR_EXPLANATION_LINK
} from '@oneethos/shared'

type LoanSummaryProps = {
  formData: any
  compareFormData?: any
  selectedEquipment?: IEquipmentOption
  incentive?: Incentive
  showApr?: boolean
  showDisclosure?: boolean
  amortization?: AmortizationSchedule
}

// Helper function to compare values and determine if they're different
const isDifferent = (value1: any, value2: any): boolean => {
  if (value1 === value2) return false
  const [v1, v2] = [value1, value2].map(v => dollarsToNum(v))
  return Math.abs(v1 - v2) > 0.01 // Small tolerance for floating point
}

const EstimatedApr = ({ formData, compareFormData, amortization }: LoanSummaryProps) => {
  const state = formData.installationAddress?.state
  const { docStamps } = LoanCalculations.estimatedClosingCostsDetail(
    dollarsToNum(formData.loanAmount),
    state,
    formData.projectType,
    formData.loanModFee
  )

  const loanTermMonths = formData.loanTermMonths || formData.loanDurationMonths

  const estimatedApr = LoanCalculations.apr(
    dollarsToNum(formData.loanAmount) + (state === 'MD' ? 0 : (docStamps || 0)),
    loanTermMonths,
    amortization?.payment
  )

  const compareState = compareFormData?.installationAddress?.state
  const compareLoanAmount = compareFormData?.loanAmount
  const compareLoanTermMonths = compareFormData?.loanTermMonths || compareFormData?.loanDurationMonths

  let compareEstimatedApr = null
  if (compareFormData && compareLoanAmount && compareLoanTermMonths) {
    const { docStamps: compareDocStamps } = LoanCalculations.estimatedClosingCostsDetail(
      dollarsToNum(compareLoanAmount),
      compareState,
      compareFormData.projectType
    )

    compareEstimatedApr = LoanCalculations.apr(
      dollarsToNum(compareLoanAmount) + (compareState === 'MD' ? 0 : (compareDocStamps || 0)),
      compareLoanTermMonths,
      amortization?.payment
    )
  }

  const isAprDifferent = compareFormData
    && compareEstimatedApr !== null
    && isDifferent(estimatedApr, compareEstimatedApr)

  return <tr className="mt-3">
    <td className='fw-bold py-1'>Estimated APR**:</td>
    <td className={`text-end fw-bold py-1 ${isAprDifferent ? 'text-primary' : ''}`}>
      {estimatedApr.toFixed(3)}%
    </td>
  </tr>
}

export const LoanSummary = ({
  formData,
  compareFormData,
  incentive,
  showApr = false,
  showDisclosure = true,
  amortization
}: LoanSummaryProps) => {
  const closingCosts = (
    incentive?.loanFees
      ? parseFloat(incentive.loanFees)
      : LoanCalculations.estimatedClosingCosts(
        dollarsToNum(formData.loanAmount),
        formData.installationAddress?.state,
        formData.projectType,
        undefined,
        formData.loanModFee
      )
  ) - (formData.loanModFee || 0)

  const amt = incentive?.amountFinanced || formData.loanAmount
  const amountFinanced = dollarsToNum(amt) + closingCosts + (formData.loanModFee || 0)
  const equipmentType = formData.equipmentType
  const isStorageOnly = equipmentType === EquipmentType.StorageOnly

  const adders = new Adders(formData.adders)
  const nonCreditAdders = adders.getNonCreditAdders(equipmentType)
  const incentiveRebates = adders.getRebatesAndOrCreditsAdder()?.amount
  const downpaymentAmount = adders.getDownpaymentAmount()

  // Calculate comparison values
  const compareClosingCosts = compareFormData ? (
    incentive?.loanFees
      ? parseFloat(incentive.loanFees)
      : LoanCalculations.estimatedClosingCosts(
        dollarsToNum(compareFormData.loanAmount),
        compareFormData.installationAddress?.state,
        compareFormData.projectType,
        undefined,
        compareFormData.loanModFee
      ) - (compareFormData.loanModFee || 0)
  ) : null

  const compareAmt = compareFormData ? (incentive?.amountFinanced || compareFormData.loanAmount) : null
  const compareAmountFinanced = compareAmt
    ? dollarsToNum(compareAmt) + (compareClosingCosts || 0) - (compareFormData.loanModFee || 0)
    : null
  const compareAdders = compareFormData ? new Adders(compareFormData.adders) : null
  const compareIncentiveRebates = compareAdders?.getRebatesAndOrCreditsAdder()?.amount
  const compareDownpaymentAmount = compareAdders?.getDownpaymentAmount()

  return (
    <div className="loan-summary">
      <table className="table table-borderless w-auto mb-1">
        <tbody>
          {isStorageOnly ? null :
            <tr>
              <td className="py-1">Solar Price:</td>
              <td className={`text-end py-1 ${compareFormData && isDifferent(formData.solarCost, compareFormData?.solarCost) ? 'text-primary fw-bold' : ''}`}>
                {dollars(formData.solarCost)}
              </td>
            </tr>
          }
          {nonCreditAdders?.map((adder, index) => {
            const compareAdder = compareAdders?.for(adder.description as AdderTypes)
            const isAdderDifferent = compareFormData && compareAdder && isDifferent(adder.amount, compareAdder.amount)

            if (!dollarsToNum(adder.amount) && !dollarsToNum(compareAdder?.amount)) {
              return null
            }

            return (
              <tr key={index}>
                <td className="py-1">{adder.description}:</td>
                <td className={`text-end py-1 ${isAdderDifferent ? 'text-primary fw-bold' : ''}`}>
                  {adder.amount}
                </td>
              </tr>
            )
          })}
          <tr className="border-top">
            <td className="py-1">Total System Cost:</td>
            <td className={`text-end py-1 ${compareFormData && isDifferent(formData.systemCost, compareFormData?.systemCost) ? 'text-primary fw-bold' : ''}`}>
              {dollars(formData.systemCost)}
            </td>
          </tr>
          <tr>
            <td className="py-1">Estimated Loan Fees*:</td>
            <td className={`text-end py-1 ${compareFormData && isDifferent(closingCosts, compareClosingCosts) ? 'text-primary fw-bold' : ''}`}>
              {dollars(closingCosts)}
            </td>
          </tr>
          {formData.loanModFee ? (
            <tr>
              <td className="py-1">Loan Modification Fee:</td>
              <td className={`text-end py-1 ${compareFormData && isDifferent(formData.loanModFee, compareFormData?.loanModFee) ? 'text-primary fw-bold' : ''}`}>
                {dollars(formData.loanModFee)}
              </td>
            </tr>
          ) : null}
          {incentiveRebates ? (
            <tr>
              <td className="py-1">{AdderTypes.RebatesAndOrCredits}</td>
              <td className={`text-end py-1 ${compareFormData && isDifferent(incentiveRebates, compareIncentiveRebates) ? 'text-primary fw-bold' : ''}`}>
                - {dollars(incentiveRebates)}
              </td>
            </tr>
          ) : null}
          {downpaymentAmount > 0 ? (
            <tr>
              <td className="py-1">Downpayment:</td>
              <td className={`text-end py-1 ${compareFormData && isDifferent(downpaymentAmount, compareDownpaymentAmount) ? 'text-primary fw-bold' : ''}`}>
                - {dollars(downpaymentAmount)}
              </td>
            </tr>
          ) : null}
          <tr className="border-top">
            <td className='fw-bold py-1'>Total Loan Amount:</td>
            <td className={`text-end fw-bold py-1 ${compareFormData && isDifferent(amountFinanced, compareAmountFinanced) ? 'text-primary' : ''}`}>
              {dollars(amountFinanced)}
            </td>
          </tr>
          {showApr && !incentive ?
            <EstimatedApr formData={formData} compareFormData={compareFormData} amortization={amortization} />
            : null}
        </tbody>
      </table>

      {showDisclosure ? <div className="text-small mt-3 fst-italic">
        {LOAN_FEE_DISCLOSURE}
      </div> : null}
      {showApr ?
        <a
          href={APR_EXPLANATION_LINK}
          className="text-small d-flex mb-3 fst-italic"
          target="_blank"
        >**How is the APR Calculated?</a> : null
      }
    </div>
  )
}

export default LoanSummary
